import { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useNotification, Notification, NotificationType, ReportSection } from '../contexts/NotificationContext';

const typeConfig: Record<NotificationType, { icon: typeof CheckCircle; bgColor: string; borderColor: string; iconColor: string; titleColor: string }> = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    titleColor: 'text-emerald-300',
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-danger/10',
    borderColor: 'border-danger/30',
    iconColor: 'text-danger',
    titleColor: 'text-danger',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    titleColor: 'text-amber-300',
  },
  info: {
    icon: Info,
    bgColor: 'bg-info/10',
    borderColor: 'border-info/30',
    iconColor: 'text-accent-info',
    titleColor: 'text-accent-info',
  },
  report: {
    icon: FileText,
    bgColor: 'bg-surface-raised/95',
    borderColor: 'border-border',
    iconColor: 'text-text-secondary',
    titleColor: 'text-text-primary',
  },
};

const sectionConfig: Record<ReportSection['type'], { icon: typeof CheckCircle; bgColor: string; borderColor: string; iconColor: string; titleColor: string; textColor: string }> = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/25',
    iconColor: 'text-emerald-400',
    titleColor: 'text-emerald-300',
    textColor: 'text-emerald-200/80',
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-danger/10',
    borderColor: 'border-danger/25',
    iconColor: 'text-danger',
    titleColor: 'text-danger',
    textColor: 'text-red-200/80',
  },
};

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

function ReportItem({ notification, onClose }: NotificationItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 200);
  };

  const toggleSection = (index: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const sections = notification.reportSections || [];

  return (
    <div
      className={`
        relative w-full max-w-lg mx-auto
        bg-surface-raised/95 border border-border
        rounded-xl shadow-2xl backdrop-blur-xl
        transform transition-all duration-200 ease-out
        ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
      `}
      role="alertdialog"
      aria-labelledby={`notification-title-${notification.id}`}
    >
      {/* Header — neutral */}
      <div className="flex items-start gap-3 p-4 pb-3 border-b border-border-subtle">
        <div className="flex-shrink-0 text-text-secondary">
          <FileText className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            id={`notification-title-${notification.id}`}
            className="text-base font-semibold text-text-primary"
          >
            {notification.title}
          </h3>
          {notification.message && (
            <p className="text-sm text-text-secondary mt-0.5">{notification.message}</p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-text-secondary hover:text-text-primary transition-colors p-1 -m-1 rounded-lg hover:bg-surface-hover/60"
          aria-label="Zamknij"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Report sections — colored */}
      <div className="p-4 space-y-3">
        {sections.map((section, index) => {
          const cfg = sectionConfig[section.type];
          const SectionIcon = cfg.icon;
          const hasItems = section.items && section.items.length > 0;
          const isExpanded = expandedSections.has(index);

          return (
            <div
              key={index}
              className={`rounded-lg border ${cfg.bgColor} ${cfg.borderColor} overflow-hidden`}
            >
              {/* Section header */}
              <div
                className={`flex items-center gap-2.5 px-3.5 py-2.5 ${hasItems ? 'cursor-pointer hover:bg-surface-hover/50 transition-colors' : ''}`}
                onClick={() => hasItems && toggleSection(index)}
              >
                <SectionIcon className={`w-5 h-5 flex-shrink-0 ${cfg.iconColor}`} />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium ${cfg.titleColor}`}>
                    {section.title}
                  </span>
                  <span className={`text-sm ml-2 ${cfg.textColor}`}>
                    {section.message}
                  </span>
                </div>
                {hasItems && (
                  <span className={`flex-shrink-0 ${cfg.iconColor}`}>
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />
                    }
                  </span>
                )}
              </div>

              {/* Expandable items list */}
              {hasItems && isExpanded && (
                <div className="px-3.5 pb-3 pt-1 border-t border-border-subtle">
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {section.items!.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                        <span className="text-text-secondary truncate max-w-[140px] flex-shrink-0">{item.label}</span>
                        {item.detail && (
                          <>
                            <span className="text-text-disabled">—</span>
                            <span className="text-text-secondary/80 break-words">{item.detail}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 pb-4">
        <button
          onClick={handleClose}
          className="w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all duration-150
            bg-surface-hover/60 hover:bg-surface-hover/70 text-text-primary border border-border"
          autoFocus
        >
          OK
        </button>
      </div>

      {/* Auto-close progress bar */}
      {notification.autoClose && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 rounded-b-xl overflow-hidden">
          <div
            className="h-full bg-gray-400 opacity-50"
            style={{
              animation: `shrink ${notification.duration || 5000}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}

function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const config = typeConfig[notification.type];
  const Icon = config.icon;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 200);
  };

  // Handle Enter key for closing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className={`
        relative w-full max-w-md mx-auto
        ${config.bgColor} ${config.borderColor}
        border rounded-xl shadow-2xl backdrop-blur-sm
        transform transition-all duration-200 ease-out
        ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
      `}
      role="alertdialog"
      aria-labelledby={`notification-title-${notification.id}`}
      aria-describedby={`notification-message-${notification.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 p-4 pb-2">
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            id={`notification-title-${notification.id}`}
            className={`text-base font-semibold ${config.titleColor}`}
          >
            {notification.title}
          </h3>
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-text-secondary hover:text-text-primary transition-colors p-1 -m-1 rounded-lg hover:bg-surface-hover/60"
          aria-label="Zamknij"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-4 pl-[52px]">
        <p
          id={`notification-message-${notification.id}`}
          className="text-sm text-text-secondary leading-relaxed"
        >
          {notification.message}
        </p>

        {/* Details */}
        {notification.details && (
          <div className="mt-3 p-3 bg-black/20 rounded-lg border border-border-subtle">
            <p className="text-xs text-text-secondary font-medium mb-1">Szczegóły:</p>
            <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono">
              {notification.details}
            </pre>
          </div>
        )}

        {/* Original error toggle */}
        {notification.originalError && (
          <div className="mt-3">
            <button
              onClick={() => setShowOriginal(!showOriginal)}
              className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary transition-colors"
            >
              {showOriginal ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Ukryj szczegóły techniczne
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  Pokaż szczegóły techniczne
                </>
              )}
            </button>
            {showOriginal && (
              <div className="mt-2 p-3 bg-black/30 rounded-lg border border-border-subtle overflow-auto max-h-32">
                <pre className="text-xs text-text-muted whitespace-pre-wrap font-mono break-all">
                  {notification.originalError}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 pl-[52px]">
        <button
          onClick={handleClose}
          className={`
            w-full py-2.5 px-4 rounded-lg font-medium text-sm
            transition-all duration-150
            ${notification.type === 'success'
              ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30'
              : notification.type === 'error'
                ? 'bg-danger/20 hover:bg-danger/30 text-danger border border-danger/30'
                : notification.type === 'warning'
                  ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30'
                  : 'bg-info/20 hover:bg-blue-500/30 text-accent-info border border-info/30'
            }
          `}
          autoFocus
        >
          OK
        </button>
      </div>

      {/* Auto-close progress bar */}
      {notification.autoClose && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 rounded-b-xl overflow-hidden">
          <div
            className={`h-full ${config.iconColor.replace('text-', 'bg-')} opacity-50`}
            style={{
              animation: `shrink ${notification.duration || 5000}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}

export function NotificationModal() {
  const { notifications, hideNotification } = useNotification();

  if (notifications.length === 0) return null;

  // Show only the most recent notification
  const currentNotification = notifications[notifications.length - 1];
  const isReport = currentNotification.type === 'report';

  return (
    <>
      {/* Global animation styles */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ animation: 'fadeIn 150ms ease-out' }}
      >
        {/* Semi-transparent overlay */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => hideNotification(currentNotification.id)}
        />

        {/* Notification container */}
        <div
          className={`relative z-10 w-full ${isReport ? 'max-w-lg' : 'max-w-md'}`}
          style={{ animation: 'slideUp 200ms ease-out' }}
        >
          {isReport ? (
            <ReportItem
              notification={currentNotification}
              onClose={() => hideNotification(currentNotification.id)}
            />
          ) : (
            <NotificationItem
              notification={currentNotification}
              onClose={() => hideNotification(currentNotification.id)}
            />
          )}

          {/* Queue indicator */}
          {notifications.length > 1 && (
            <div className="mt-2 text-center">
              <span className="text-xs text-text-muted">
                +{notifications.length - 1} {notifications.length === 2 ? 'kolejny komunikat' : 'kolejnych komunikatów'}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default NotificationModal;
